import { fromUnixTime, getUnixTime, parseISO, startOfDay } from 'date-fns';
import { formatYearMonthDay } from 'src/utils/dateFormatter';

interface DateFilterObject {
  date?: string;
  startDate?: string;
  endDate?: string;
  dateFilter?: string;
  utc?: boolean;
}

interface AssociationObject {
  id: string | number;
  [key: string]: any;
}

export const unstringifyBooleans = (
  obj: Record<string, any>,
  booleanKeys: string[]
): Record<string, any> => {
  const objToReturn = { ...obj };
  for (const prop in objToReturn) {
    if (Object.hasOwn(objToReturn, prop)) {
      if (booleanKeys.includes(prop)) {
        objToReturn[prop] = JSON.parse(objToReturn[prop]);
      }
    }
  }
  return objToReturn;
};

export const unNullifyArrays = (
  obj: Record<string, any>,
  arrayKeys: string[]
): Record<string, any> => {
  const objToReturn = { ...obj };
  for (const prop in objToReturn) {
    if (Object.hasOwn(objToReturn, prop)) {
      if (arrayKeys.includes(prop)) {
        objToReturn[prop] ||= [];
      }
    }
  }
  return objToReturn;
};

export const dateToTimestamp = (date?: Date | null): number | null => {
  if (date) {
    return getUnixTime(date);
  } else {
    return null;
  }
};

export function timestampToDate(timestamp: number): Date;
export function timestampToDate(timestamp?: number): Date | null;
export function timestampToDate(timestamp?: number): Date | null {
  if (timestamp) {
    return fromUnixTime(timestamp);
  } else {
    return null;
  }
}

export const stringToDate = (string?: string): Date | null => {
  if (string) {
    return startOfDay(parseISO(string));
  } else {
    return null;
  }
};

export const dateToString = (date?: Date | null): string | null => {
  if (date) {
    // Use local date methods to avoid timezone conversion issues
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  } else {
    return null;
  }
};

export const centsToDollars = (cents: number): number => {
  return cents / 100.0;
};

export const dollarsToCents = (dollars: number): number => {
  return Math.round(dollars * 100);
};

export const removeReadOnlyFields = (
  obj: Record<string, any>,
  fields: string[]
): void => {
  fields.forEach((field) => {
    delete obj[field];
  });
};

export const associationsToIds = (
  obj: Record<string, any>,
  associationNames: string[]
): Record<string, any> => {
  return associationNames.reduce((acc, assocName) => {
    return associationToIds(acc, assocName);
  }, obj);
};

export const associationsToId = (
  obj: Record<string, any>,
  associationNames: string[]
): Record<string, any> => {
  return associationNames.reduce((acc, assocName) => {
    return associationToId(acc, assocName);
  }, obj);
};

/**
 * Processes date filter parameters from URL params (f_date, f_secondaryDate, etc.)
 * and converts them to the format expected by the backend
 */
export const processDateFilter = (
  params: Record<string, any>,
  fieldName: string,
  outputPrefix = ''
): void => {
  const inputKey = `f_${fieldName}`;
  const prefix = outputPrefix ? `${outputPrefix}_` : '';

  if (params[inputKey]) {
    try {
      // Parse the encoded JSON date filter object
      const dateFilter: DateFilterObject = JSON.parse(
        decodeURIComponent(params[inputKey])
      );

      // Handle single date (used for both start and end)
      if (dateFilter.date) {
        params[`${prefix}start_date`] = getUnixTime(new Date(dateFilter.date));
        params[`${prefix}start_date_string`] = formatYearMonthDay(
          dateFilter.date
        );
        params[`${prefix}end_date`] = getUnixTime(new Date(dateFilter.date));
        params[`${prefix}end_date_string`] = formatYearMonthDay(
          dateFilter.date
        );
      }

      // Handle start date
      if (dateFilter.startDate) {
        params[`${prefix}start_date`] = getUnixTime(
          new Date(dateFilter.startDate)
        );
        params[`${prefix}start_date_string`] = formatYearMonthDay(
          dateFilter.startDate
        );
      }

      // Handle end date
      if (dateFilter.endDate) {
        params[`${prefix}end_date`] = getUnixTime(new Date(dateFilter.endDate));
        params[`${prefix}end_date_string`] = formatYearMonthDay(
          dateFilter.endDate
        );
      }

      // Handle additional date filter properties (only for main date, not secondary)
      if (!outputPrefix) {
        if (dateFilter.dateFilter) {
          params.date_filter = dateFilter.dateFilter;
        }

        if (dateFilter.utc) {
          params.utc = dateFilter.utc;
        }
      }

      // Remove the original encoded parameter
      delete params[inputKey];
    } catch (e) {
      // Silently ignore parsing errors
    }
  }
};

function associationToIds(
  json: Record<string, any>,
  associationName: string
): Record<string, any> {
  const associationIds =
    json[associationName]?.map((a: AssociationObject) => a.id) || [];
  const associationKey = `${associationName.slice(0, -1)}_ids`;

   
  const { [associationName]: _, ...rest } = json;

  // Returning a new object with the association ids and the rest of the properties
  return {
    ...rest,
    [associationKey]: json[associationKey] || associationIds,
  };
}

function associationToId(
  json: Record<string, any>,
  associationName: string
): Record<string, any> {
  const associationId = json[associationName]?.id;
  const associationKey = `${associationName}_id`;

   
  const { [associationName]: _, ...rest } = json;

  // Returning a new object with the association ids and the rest of the properties
  return {
    ...rest,
    [associationKey]: json[associationKey] || associationId,
  };
}
